/**
 * Password validation utility functions
 */

export interface PasswordCondition {
    key: string;
    label: string;
    test: (password: string) => boolean;
    met: boolean;
}

/**
 * Validates if password meets all strength requirements
 * @param password The password to validate
 * @returns true if password meets all requirements, false otherwise
 */
export function validatePassword(password: string): boolean {
    return (
        password.length >= 12 &&
        /[A-Z]/.test(password) &&
        /[a-z]/.test(password) &&
        /\d/.test(password) &&
        /[^a-zA-Z0-9]/.test(password)
    );
}

/**
 * Gets all password conditions with their current state
 * @param password The password to check against
 * @returns Array of password conditions with met status
 */
export function getPasswordConditions(password: string): PasswordCondition[] {
    return [
        {
            key: 'length',
            label: 'At least 12 characters',
            test: (p: string) => p.length >= 12,
            met: password.length >= 12
        },
        {
            key: 'uppercase',
            label: 'At least one uppercase letter (A-Z)',
            test: (p: string) => /[A-Z]/.test(p),
            met: /[A-Z]/.test(password)
        },
        {
            key: 'lowercase',
            label: 'At least one lowercase letter (a-z)',
            test: (p: string) => /[a-z]/.test(p),
            met: /[a-z]/.test(password)
        },
        {
            key: 'digit',
            label: 'At least one number (0-9)',
            test: (p: string) => /\d/.test(p),
            met: /\d/.test(password)
        },
        {
            key: 'special',
            label: 'At least one special character (!@#$%^&*)',
            test: (p: string) => /[^a-zA-Z0-9]/.test(p),
            met: /[^a-zA-Z0-9]/.test(password)
        }
    ];
}

/**
 * Checks if all password conditions are met
 * @param password The password to validate
 * @returns true if all conditions are met
 */
export function allPasswordConditionsMet(password: string): boolean {
    return getPasswordConditions(password).every((condition) => condition.met);
}
